import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { firmaservice } from '../../../core/services/firmas.service';
import { ActaEntregaEppService } from '../../services/acta-entrega-epp.service';
import { DetalleActaEntregaService } from '../../services/detalle-acta-entrega.service';
import { EmpleadoService } from '../../../empresa/services/empleado.service';
import { InventarioService } from '../../services/inventario.service';
import { CargoService } from '../../../empresa/services/cargo.service';
import { lastValueFrom, from } from 'rxjs';
import { FilterQuery } from '../../../core/entities/filter-query';
import { Criteria } from '../../../core/entities/filter';
import { ActaEntregaEpp } from '../../entities/acta-entrega-epp';
import { StockService } from '../../services/stock.service';
import { FichaTecnicaEppService } from '../../../epp/services/ficha-tecnica-epp.service';

interface TokenPayload {
  sub: string;
  idRelacionado: string;
  iat: number;
  exp: number;
}

interface ActaCompleta {
  id: number;
  numeroActa: string;
  fechaEntrega: Date;
  lugarEntrega: string;
  tipoEntrega: string;
  observaciones: string;
  estado: string;
  empleadoRecibe: {
    id: number;
    nombre: string;
    cargo: string;
    area: string;
    email: string;
  };
  empleadoEntrega: {
    id: number;
    nombre: string;
    cargo: string;
    area: string;
    email: string;
  };
  bodega: {
    id: number;
    nombre: string;
  };
  elementos: Array<{
    id: number;
    nombre: string;
    descripcion: string;
    cantidad: number;
    fichaTecnica: {
      titulo: string;
      certificado: string;
    };
  }>;
}

@Component({
  selector: 'app-firma-acta-epp',
  templateUrl: './firma-acta-epp.component.html',
  styleUrls: ['./firma-acta-epp.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    PanelModule,
    TableModule
  ],
  providers: [MessageService]
})
export class FirmaActaEppComponent implements OnInit {
  @ViewChild('firmaCanvas') firmaCanvas!: ElementRef<HTMLCanvasElement>;
  
  token: string | null = null;
  actaData: ActaCompleta | null = null;
  isDrawing = false;
  firmaForm: FormGroup;
  loading = false;
  tokenPayload: TokenPayload | null = null;
  yaFirmado = false; // Nueva propiedad para controlar si ya está firmado
  firmaExistente: string | null = null; // Propiedad para almacenar la firma existente

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private firmaService: firmaservice,
    private actaService: ActaEntregaEppService,
    private detalleService: DetalleActaEntregaService,
    private empleadoService: EmpleadoService,
    private inventarioService: InventarioService,
    private cargoService: CargoService,
    private messageService: MessageService,
    private stockService: StockService,
    private fichaTecnicaService: FichaTecnicaEppService
  ) {
    this.firmaForm = this.fb.group({
      firma: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token');
    if (this.token) {
      this.validarToken();
    } else {
      this.mostrarError('Token no válido');
    }
  }

  private validarToken() {
    try {
      // Decodificar el token JWT (solo la parte del payload)
      const tokenParts = this.token!.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Token malformado');
      }

      const payload = JSON.parse(atob(tokenParts[1])) as TokenPayload;
      this.tokenPayload = payload;

      // Verificar si el token ha expirado
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        throw new Error('Token expirado');
      }

      console.log('Token válido, payload:', payload);
      this.cargarDatosActa();
    } catch (error) {
      console.error('Error validando token:', error);
      this.mostrarError('Token inválido o expirado');
    }
  }

  private async cargarDatosActa() {
    try {
      this.loading = true;
      
      if (!this.tokenPayload) {
        throw new Error('Token payload no disponible');
      }

      // Extraer información del idRelacionado: "ACTA-20250726-235820803-7710,5438,recibe"
      const [numeroActa, idEmpleadoRecibe] = this.tokenPayload.idRelacionado.split(',');
      
      console.log('Cargando acta:', numeroActa);
      console.log('Empleado que recibe:', idEmpleadoRecibe);

      // Buscar el acta por número
      const filterQuery = new FilterQuery();
      filterQuery.filterList = [
        {
          criteria: Criteria.EQUALS,
          field: 'numeroActa',
          value1: numeroActa,
          value2: null
        }
      ];

      const actaResponse = await lastValueFrom(from(this.actaService.findByFilter(filterQuery)));
      const acta = (actaResponse as any)?.data?.[0];
      
      if (!acta) {
        throw new Error('Acta no encontrada');
      }

      console.log('Acta encontrada:', acta);

      // Cargar empleado que recibe
      const empleadoRecibe = await this.cargarEmpleado(acta.idEmpleadoRecibe);
      
      // Cargar empleado que entrega
      const empleadoEntrega = await this.cargarEmpleado(acta.idEmpleadoEntrega);
      
      // Cargar bodega
      const bodega = await this.cargarBodega(acta.idInventario);
      
      // Cargar detalles del acta
      const detalles = await this.cargarDetalles(acta.id);

      // Construir objeto completo
      this.actaData = {
        id: acta.id,
        numeroActa: acta.numeroActa,
        fechaEntrega: new Date(acta.fechaEntrega),
        lugarEntrega: acta.lugarEntrega,
        tipoEntrega: acta.tipoEntrega,
        observaciones: acta.observaciones || '',
        estado: acta.estado,
        empleadoRecibe,
        empleadoEntrega,
        bodega,
        elementos: detalles
      };

      // Verificar si ya tiene firma
      this.yaFirmado = !!(acta.firmaRecibe && acta.firmaRecibe.trim() !== '');
      this.firmaExistente = acta.firmaRecibe; // Almacenar la firma existente
      
      if (this.yaFirmado) {
        this.messageService.add({
          severity: 'info',
          summary: 'Acta ya firmada',
          detail: 'Este acta ya ha sido firmado anteriormente. Los elementos han sido entregados.'
        });
      }

      console.log('Datos del acta cargados:', this.actaData);
      console.log('¿Ya firmado?:', this.yaFirmado);
      this.loading = false;

    } catch (error) {
      console.error('Error cargando datos del acta:', error);
      this.mostrarError('Error cargando los datos del acta');
      this.loading = false;
    }
  }

  private async cargarEmpleado(idEmpleado: number): Promise<any> {
    try {
      console.log('Cargando empleado con ID:', idEmpleado);
      
      const filterQuery = new FilterQuery();
      filterQuery.filterList = [
        {
          criteria: Criteria.EQUALS,
          field: 'id',
          value1: idEmpleado.toString(),
          value2: null
        }
      ];

      const response = await lastValueFrom(from(this.empleadoService.findByFilter(filterQuery)));
      console.log('Respuesta empleado service:', response);
      
      const empleado = (response as any)?.data?.[0];
      console.log('Empleado encontrado:', empleado);
      
      if (!empleado) {
        console.warn('Empleado no encontrado para ID:', idEmpleado);
        return {
          id: idEmpleado,
          nombre: 'Empleado no encontrado',
          cargo: 'N/A',
          area: 'N/A',
          email: 'N/A'
        };
      }

      // Construir nombre completo usando los campos correctos
      const nombreCompleto = [
        empleado.primerNombre,
        empleado.segundoNombre,
        empleado.primerApellido,
        empleado.segundoApellido
      ].filter(n => n && n.trim()).join(' ');

      // Obtener información del cargo
      let cargo = 'N/A';
      let area = 'N/A';
      
      try {
        // Intentar obtener el cargo del empleado
        const cargoFilter = new FilterQuery();
        cargoFilter.filterList = [
          {
            criteria: Criteria.EQUALS,
            field: 'empleado.id',
            value1: idEmpleado.toString(),
            value2: null
          }
        ];
        
        const cargoResponse = await lastValueFrom(from(this.cargoService.findByFilter(cargoFilter)));
        const cargoData = (cargoResponse as any)?.data?.[0];
        
        if (cargoData) {
          cargo = cargoData.nombre || 'N/A';
          area = cargoData.area?.nombre || 'N/A';
          console.log('Cargo encontrado:', cargoData);
        }
      } catch (cargoError) {
        console.warn('Error cargando cargo del empleado:', cargoError);
        // Usar información del jefe inmediato como fallback
        if (empleado.jefeInmediato) {
          cargo = empleado.jefeInmediato.cargo?.nombre || 'N/A';
          area = empleado.jefeInmediato.area?.nombre || 'N/A';
        }
      }

      // Obtener email del usuario
      const email = empleado.usuario?.email || empleado.correoPersonal || 'N/A';

      const empleadoInfo = {
        id: empleado.id,
        nombre: nombreCompleto || 'Sin nombre',
        cargo: cargo,
        area: area,
        email: email
      };

      console.log('Información del empleado procesada:', empleadoInfo);
      return empleadoInfo;
    } catch (error) {
      console.error('Error cargando empleado:', error);
      return {
        id: idEmpleado,
        nombre: 'Error cargando empleado',
        cargo: 'N/A',
        area: 'N/A',
        email: 'N/A'
      };
    }
  }

  private async cargarBodega(idInventario: number): Promise<any> {
    try {
      const filterQuery = new FilterQuery();
      filterQuery.filterList = [
        {
          criteria: Criteria.EQUALS,
          field: 'id',
          value1: idInventario.toString(),
          value2: null
        }
      ];

      const response = await lastValueFrom(from(this.inventarioService.findByFilter(filterQuery)));
      const inventario = (response as any)?.data?.[0];
      
      if (!inventario) {
        return {
          id: idInventario,
          nombre: 'Bodega no encontrada'
        };
      }

      return {
        id: inventario.id,
        nombre: inventario.bodega || 'Sin nombre'
      };
    } catch (error) {
      console.error('Error cargando bodega:', error);
      return {
        id: idInventario,
        nombre: 'Error cargando bodega'
      };
    }
  }

  private async cargarDetalles(idActa: number): Promise<any[]> {
    try {
      console.log('Cargando detalles para acta ID:', idActa);
      
      // Usar directamente el método con filtros ya que el endpoint específico no existe
      const response = await lastValueFrom(from(this.detalleService.getDetallesByActaFilter(idActa)));
      const detalles = (response as any)?.data || [];
      
      console.log('Detalles encontrados con filtros:', detalles);

      // Mapear los detalles con información más útil
      return detalles.map((detalle: any) => ({
        id: detalle.id,
        nombre: detalle.observaciones ? detalle.observaciones.split(',')[0].replace('Elemento: ', '') : `Elemento ID: ${detalle.idDetalleSolicitudEpp}`,
        descripcion: detalle.observaciones || 'Sin descripción',
        cantidad: detalle.cantidadEntregada,
        fichaTecnica: {
          titulo: detalle.observaciones ? detalle.observaciones.split(',')[1]?.replace(' Ficha Técnica: ', '') : 'Información no disponible',
          certificado: 'N/A'
        }
      }));
    } catch (error) {
      console.error('Error cargando detalles:', error);
      
      // Retornar datos de ejemplo si falla
      return [
        {
          id: 1,
          nombre: 'Elemento EPP',
          descripcion: 'Información no disponible temporalmente',
          cantidad: 1,
          fichaTecnica: {
            titulo: 'Información no disponible',
            certificado: 'N/A'
          }
        }
      ];
    }
  }

  private mostrarError(mensaje: string) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: mensaje
    });
    setTimeout(() => {
      this.router.navigate(['/']);
    }, 3000);
  }

  startDrawing(event: MouseEvent) {
    this.isDrawing = true;
    const canvas = this.firmaCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const rect = canvas.getBoundingClientRect();
      ctx.beginPath();
      ctx.moveTo(event.clientX - rect.left, event.clientY - rect.top);
    }
  }

  draw(event: MouseEvent) {
    if (!this.isDrawing) return;
    const canvas = this.firmaCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const rect = canvas.getBoundingClientRect();
      ctx.lineTo(event.clientX - rect.left, event.clientY - rect.top);
      ctx.stroke();
    }
  }

  stopDrawing() {
    this.isDrawing = false;
  }

  limpiarFirma() {
    const canvas = this.firmaCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  async guardarFirma() {
    try {
      if (!this.token || !this.actaData) {
        throw new Error('Datos incompletos');
      }

      const canvas = this.firmaCanvas.nativeElement;
      const firmaDataUrl = canvas.toDataURL();
      
      // Actualizar el acta con la firma usando el servicio de actas
      const actaActualizada: ActaEntregaEpp = {
        id: this.actaData.id,
        numeroActa: this.actaData.numeroActa,
        idSolicitudEpp: 1, // Valor por defecto, debería venir del acta original
        idEmpleadoEntrega: this.actaData.empleadoEntrega.id,
        idEmpleadoRecibe: this.actaData.empleadoRecibe.id,
        fechaEntrega: this.actaData.fechaEntrega,
        lugarEntrega: this.actaData.lugarEntrega,
        tipoEntrega: this.actaData.tipoEntrega as 'PERSONAL' | 'CORREO',
        observaciones: this.actaData.observaciones,
        firmaRecibe: firmaDataUrl,
        estado: 'ENTREGADO' as 'PENDIENTE' | 'ENTREGADO' | 'ENVIADO' | 'RECIBIDO',
        idEmpresa: 22, // Valor por defecto, debería venir del acta original
        idInventario: this.actaData.bodega.id
      };

      await lastValueFrom(from(this.actaService.update(actaActualizada)));

      // Descontar cantidades del inventario
      await this.descontarStockInventario();

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Firma guardada y stock actualizado correctamente. Los elementos han sido entregados.'
      });

      // Recargar los datos del acta para actualizar el estado
      setTimeout(async () => {
        await this.cargarDatosActa();
      }, 1000);

    } catch (error) {
      console.error('Error guardando firma:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error guardando la firma'
      });
    }
  }

  private async descontarStockInventario() {
    try {
      console.log('🏪 Iniciando descuento de stock del inventario...');
      
      if (!this.actaData || !this.actaData.elementos || this.actaData.elementos.length === 0) {
        console.log('⚠️ No hay elementos para descontar');
        return;
      }

      const idInventario = this.actaData.bodega.id;
      console.log('📦 Inventario ID:', idInventario);

      // Obtener el stock actual del inventario usando el método específico
      const stockResponse = await lastValueFrom(from(this.stockService.findByIdInventario(idInventario)));
      const stockActual = (stockResponse as any)?.data || stockResponse || [];
      
      console.log('📊 Stock actual encontrado:', stockActual.length, 'elementos');

      // Obtener las fichas técnicas para poder hacer la comparación correcta
      const stockConFichas = await this.enriquecerStockConFichas(stockActual);
      console.log('📋 Stock enriquecido con fichas técnicas:', stockConFichas);

      // Procesar cada elemento del acta
      for (const elemento of this.actaData.elementos) {
        console.log('🔄 Procesando elemento:', elemento.nombre, 'Cantidad:', elemento.cantidad);
        console.log('📝 Elemento completo:', elemento);
        
        // Buscar el stock correspondiente al elemento
        let stockItem = this.buscarStockPorElemento(stockConFichas, elemento);
        
        if (stockItem) {
          console.log(`✅ Encontrado stock para: ${elemento.nombre}`);
          console.log('📦 Stock encontrado:', stockItem);
          
          // Verificar que hay suficiente stock
          const cantidadActual = stockItem.cantidad || 0;
          if (cantidadActual < elemento.cantidad) {
            console.warn(`⚠️ Stock insuficiente para ${elemento.nombre}. Disponible: ${cantidadActual}, Requerido: ${elemento.cantidad}`);
            this.messageService.add({
              severity: 'warn',
              summary: 'Stock insuficiente',
              detail: `Stock insuficiente para ${elemento.nombre}. Disponible: ${cantidadActual}, Requerido: ${elemento.cantidad}`
            });
            continue;
          }
          
          // Calcular nueva cantidad
          const nuevaCantidad = Math.max(0, cantidadActual - elemento.cantidad);
          
          // Actualizar el stock - limpiar el objeto para el backend
          const stockActualizado = {
            id: stockItem.id,
            idInventario: stockItem.idInventario,
            idFichaTecnica: stockItem.idFichaTecnica,
            idUnidadMedida: stockItem.idUnidadMedida,
            cantidad: nuevaCantidad,
            idEstadoInventario: stockItem.idEstadoInventario,
            precioUnitario: stockItem.precioUnitario,
            fecha: stockItem.fecha,
            minimo: stockItem.minimo,
            optimo: stockItem.optimo,
            maximo: stockItem.maximo,
            fechaCaducidad: stockItem.fechaCaducidad,
            fechaVencimiento: stockItem.fechaVencimiento,
            lote: stockItem.lote,
            disposicionFinal: stockItem.disposicionFinal,
            fechaDisposicion: stockItem.fechaDisposicion,
            motivoBaja: stockItem.motivoBaja,
            observacionesDisposicion: stockItem.observacionesDisposicion,
            estadoFisico: stockItem.estadoFisico
          };

          await lastValueFrom(from(this.stockService.update(stockActualizado)));
          console.log(`✅ Stock actualizado para ${elemento.nombre}: ${cantidadActual} → ${nuevaCantidad}`);
          
          this.messageService.add({
            severity: 'success',
            summary: 'Stock actualizado',
            detail: `${elemento.nombre}: ${cantidadActual} → ${nuevaCantidad} unidades`
          });
        } else {
          console.warn(`❌ No se encontró stock para el elemento: ${elemento.nombre} (ID: ${elemento.id})`);
          console.log('🔍 Elementos disponibles en stock:', stockConFichas.map((item: any) => ({
            id: item.id,
            idFichaTecnica: item.idFichaTecnica,
            fichaTecnica: item.fichaTecnica?.titulo,
            cantidad: item.cantidad
          })));
          this.messageService.add({
            severity: 'error',
            summary: 'Stock no encontrado',
            detail: `No se encontró stock para ${elemento.nombre}`
          });
        }
      }

      console.log('✅ Proceso de descuento de stock completado');
    } catch (error) {
      console.error('❌ Error descontando stock:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error en stock',
        detail: 'Error al actualizar el stock del inventario'
      });
      throw new Error('Error al actualizar el stock del inventario');
    }
  }

  private async enriquecerStockConFichas(stockActual: any[]): Promise<any[]> {
    try {
      console.log('🔍 Enriqueciendo stock con fichas técnicas...');
      
      const stockEnriquecido = [];
      
      for (const stockItem of stockActual) {
        try {
          // Obtener la ficha técnica para este stock
          const fichaResponse = await lastValueFrom(from(this.fichaTecnicaService.finById(stockItem.idFichaTecnica)));
          const fichaTecnica = (fichaResponse as any)?.data || fichaResponse;
          
          stockEnriquecido.push({
            ...stockItem,
            fichaTecnica: fichaTecnica
          });
          
          console.log(`📋 Ficha técnica ${stockItem.idFichaTecnica}:`, fichaTecnica);
        } catch (error) {
          console.warn(`⚠️ Error obteniendo ficha técnica ${stockItem.idFichaTecnica}:`, error);
          stockEnriquecido.push({
            ...stockItem,
            fichaTecnica: null
          });
        }
      }
      
      return stockEnriquecido;
    } catch (error) {
      console.error('❌ Error enriqueciendo stock:', error);
      return stockActual; // Retornar stock original si falla
    }
  }

  private buscarStockPorElemento(stockActual: any[], elemento: any): any {
    // Estrategia 1: Buscar por ID del detalle de solicitud
    let stockItem = stockActual.find((item: any) => item.idDetalleSolicitudEpp === elemento.id);
    if (stockItem) {
      console.log('📍 Encontrado por ID detalle solicitud');
      return stockItem;
    }

    // Estrategia 2: Buscar por ID de ficha técnica
    if (elemento.fichaTecnica?.id) {
      stockItem = stockActual.find((item: any) => item.idFichaTecnica === elemento.fichaTecnica.id);
      if (stockItem) {
        console.log('📍 Encontrado por ID ficha técnica');
        return stockItem;
      }
    }

    // Estrategia 3: Buscar por título de ficha técnica
    if (elemento.fichaTecnica?.titulo) {
      stockItem = stockActual.find((item: any) => {
        return item.fichaTecnica && 
               item.fichaTecnica.titulo && 
               item.fichaTecnica.titulo.toLowerCase() === elemento.fichaTecnica.titulo.toLowerCase();
      });
      if (stockItem) {
        console.log('📍 Encontrado por título ficha técnica');
        return stockItem;
      }
    }

    // Estrategia 4: Buscar por nombre del elemento en descripción
    if (elemento.descripcion) {
      const nombreElemento = elemento.descripcion.split(',')[0]?.replace('Elemento: ', '').trim();
      if (nombreElemento) {
        stockItem = stockActual.find((item: any) => {
          return item.fichaTecnica && 
                 item.fichaTecnica.titulo && 
                 item.fichaTecnica.titulo.toLowerCase().includes(nombreElemento.toLowerCase());
        });
        if (stockItem) {
          console.log('📍 Encontrado por nombre en descripción');
          return stockItem;
        }
      }
    }

    // Estrategia 5: Buscar por nombre del elemento
    if (elemento.nombre) {
      stockItem = stockActual.find((item: any) => {
        return item.fichaTecnica && 
               item.fichaTecnica.titulo && 
               item.fichaTecnica.titulo.toLowerCase().includes(elemento.nombre.toLowerCase());
      });
      if (stockItem) {
        console.log('📍 Encontrado por nombre del elemento');
        return stockItem;
      }
    }

    console.log('❌ No se encontró stock para el elemento:', elemento.nombre);
    console.log('📋 Elementos disponibles en stock:', stockActual.map((item: any) => ({
      id: item.id,
      idFichaTecnica: item.idFichaTecnica,
      fichaTecnica: item.fichaTecnica?.titulo,
      cantidad: item.cantidad
    })));
    
    return null;
  }

}
